'use client';

import { useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { DocsPanel } from '@/components/docs/docs-ui';
import { useDocsScroll } from '@/components/docs/docs-scroll-context';
import { docsHashPath, docsNavGroups, scrollToDocsSection } from '@/lib/docs-content';

export function DocsSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { activeSidebarSlug, reachedSidebarIndex, sidebarProgress } = useDocsScroll();
  const activeRef = useRef<HTMLAnchorElement | null>(null);
  let itemIndex = -1;

  useEffect(() => {
    const link = activeRef.current;
    const drawer = link?.closest<HTMLElement>('.docs-layout__drawer');
    if (!link || !drawer) return;
    const top = link.offsetTop - drawer.clientHeight / 2 + link.offsetHeight / 2;
    drawer.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }, [activeSidebarSlug]);

  const handleSectionNav = (slug: string) => {
    scrollToDocsSection(slug === 'introduction' ? 'introduction' : slug);
    onNavigate?.();
  };

  return (
    <DocsPanel className="p-3 sm:p-4">
      <div className="docs-sidebar-journey mb-4 hidden lg:block">
        <p className="docs-sidebar__group-title">Navigation</p>
        <p className="display mt-2 text-[1.05rem] leading-tight tracking-tight">
          <span className="text-muted block">From intro</span>
          <span className="text-accent-gradient block">to production</span>
        </p>
        <div className="readout text-faint mt-3 flex items-center gap-2 text-[0.62rem]">
          <span className="text-accent tabular-nums">{Math.round(sidebarProgress * 100)}%</span>
          <div className="bg-line-2 relative h-px flex-1 overflow-hidden">
            <motion.span
              className="bg-accent absolute inset-y-0 left-0 block shadow-[0_0_8px_var(--color-accent)]"
              animate={{ width: `${sidebarProgress * 100}%` }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <span>scroll progress</span>
        </div>
      </div>

      <nav aria-label="Documentation" className="docs-sidebar-rail relative">
        <div aria-hidden className="docs-sidebar-rail__track">
          <div className="docs-sidebar-rail__line" />
          <motion.div
            className="docs-sidebar-rail__fill"
            animate={{ height: `${sidebarProgress * 100}%` }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>

        <div className="docs-sidebar-rail__content flex flex-col gap-5">
          {docsNavGroups.map((group) => (
            <div key={group.title}>
              <p className="docs-sidebar__group-title">{group.title}</p>
              <ul className="mt-2 space-y-0.5">
                {group.items.map((item) => {
                  itemIndex += 1;
                  const index = itemIndex;
                  const active = activeSidebarSlug === item.slug;
                  const reached = index <= reachedSidebarIndex;

                  return (
                    <li key={item.slug} className="docs-sidebar-rail__item relative">
                      <span
                        aria-hidden
                        className={`docs-sidebar-rail__dot ${reached ? 'docs-sidebar-rail__dot--reached' : ''} ${
                          active ? 'docs-sidebar-rail__dot--active' : ''
                        }`}
                      />
                      <a
                        ref={active ? activeRef : null}
                        href={docsHashPath(item.slug)}
                        onClick={(event) => {
                          event.preventDefault();
                          handleSectionNav(item.slug);
                        }}
                        className={`docs-sidebar__link notch-sm relative block px-3 py-2 transition-all duration-300 ${
                          active ? 'docs-sidebar__link--active' : reached ? 'docs-sidebar__link--reached' : ''
                        }`}
                        aria-current={active ? 'page' : undefined}
                      >
                        <span className="block text-[0.8125rem] font-medium leading-snug">{item.label}</span>
                        {item.description ? (
                          <span className="docs-sidebar__description mt-0.5 hidden text-[0.6875rem] leading-snug xl:block">
                            {item.description}
                          </span>
                        ) : null}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          <div className="border-line border-t pt-5">
            <p className="docs-sidebar__group-title">Resources</p>
            <ul className="mt-2 space-y-2 text-sm">
              <li>
                <Link to="/" onClick={onNavigate} className="text-muted hover:text-accent inline-flex items-center gap-1.5 transition-colors">
                  ← Back to home
                </Link>
              </li>
              <li>
                <Link to="/dashboard/developers" onClick={onNavigate} className="text-muted hover:text-accent transition-colors">
                  Dashboard → Developers
                </Link>
              </li>
              <li>
                <Link to="/contact" onClick={onNavigate} className="text-muted hover:text-accent transition-colors">
                  Contact support
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </DocsPanel>
  );
}
